import { useCallback, useEffect, useRef } from "react";

/** Throttle: only update lastActivity at most once per second. */
const ACTIVITY_THROTTLE_MS = 1000;
/** How often to check idle state (ms). Use shorter interval when threshold is small (e.g. testing). */
const CHECK_INTERVAL_MS = 30_000;
const CHECK_INTERVAL_MS_MIN = 2_000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart",
] as const;

export interface UseIdleTimeoutOptions {
  /** Idle threshold before logout (e.g. 15 * 60 * 1000 for 15 minutes). */
  idleThresholdMs: number;
  /** Show warning this many ms before logout (e.g. 5 * 60 * 1000 for 5 minutes). */
  warningBeforeMs: number;
  /** Called when user has been idle long enough to show warning (T-5 mins). Receives remaining ms until logout. */
  onWarning: (remainingMs: number) => void;
  /** Called when idle threshold is reached (T-0) — perform logout. */
  onLogout: () => void;
  /** When false, no timers or listeners are active. */
  enabled: boolean;
}

/**
 * Tracks user activity and enforces idle session timeout for OFFICIAL-SENSITIVE data.
 * NCSC recommendation: 15 min idle threshold, warning at T-5 mins, auto-logout at T-0.
 * Uses visibilitychange so when the tab becomes visible again after being backgrounded,
 * idle time is re-checked and warning/logout can fire immediately.
 */
export function useIdleTimeout(options: UseIdleTimeoutOptions): { reset: () => void } {
  const {
    idleThresholdMs,
    warningBeforeMs,
    onWarning,
    onLogout,
    enabled,
  } = options;

  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const logoutFiredRef = useRef(false);
  const logoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastResetRef = useRef(0);

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    logoutFiredRef.current = false;
    if (logoutTimeoutRef.current !== null) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < ACTIVITY_THROTTLE_MS) return;
      lastResetRef.current = now;
      lastActivityRef.current = now;
    };

    const check = () => {
      if (logoutFiredRef.current) return;
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;

      if (elapsed >= idleThresholdMs) {
        logoutFiredRef.current = true;
        onLogout();
        return;
      }

      if (
        elapsed >= idleThresholdMs - warningBeforeMs &&
        !warningShownRef.current
      ) {
        warningShownRef.current = true;
        const remaining = idleThresholdMs - elapsed;
        onWarning(remaining);
        logoutTimeoutRef.current = setTimeout(() => {
          logoutTimeoutRef.current = null;
          if (!logoutFiredRef.current) {
            logoutFiredRef.current = true;
            onLogout();
          }
        }, remaining);
      }
    };

    const checkIntervalMs =
      idleThresholdMs < 60_000
        ? CHECK_INTERVAL_MS_MIN
        : Math.min(CHECK_INTERVAL_MS, Math.floor(idleThresholdMs / 3));
    ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, handleActivity));
    checkIntervalRef.current = setInterval(check, checkIntervalMs);
    check();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        check();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        document.removeEventListener(ev, handleActivity)
      );
      if (checkIntervalRef.current !== null) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (logoutTimeoutRef.current !== null) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [
    enabled,
    idleThresholdMs,
    warningBeforeMs,
    onWarning,
    onLogout,
  ]);

  return { reset };
}
